// Dok 07 Tredje man och pågående verksamhet: bebodda hus, verksamhet i drift, nattarbete.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";

// Tredje man ser olika ut i olika miljöer – checklistan anpassas efter typ.
const PLAN_KINDS: { value: string; label: string; hints: string[] }[] = [
  {
    value: "building",
    label: "Bostad och bebott hus",
    hints: [
      "Evakueringsplan och tillfälliga bostäder",
      "Tillfälliga kök, duschar och toaletter",
      "Trapphus och utrymningsvägar hålls framkomliga",
      "Information till boende före varje moment",
    ],
  },
  {
    value: "traffic",
    label: "Trafik och allmän plats",
    hints: [
      "TA-plan och godkännande från väghållaren",
      "Gång- och cykelpassager förbi arbetsområdet",
      "Skydd mot påkörning och avstängningar",
      "Kollektivtrafik, hållplatser och utryckningsvägar",
    ],
  },
  {
    value: "healthcare",
    label: "Vård, skola och omsorg",
    hints: [
      "Verksamhetens drift, patientsäkerhet och tysta tider",
      "Smitt- och dammskydd samt undertryck",
      "Larm, utrymning och medicinska gaser",
      "Barn och personer med nedsatt rörlighet i närheten",
    ],
  },
  {
    value: "industrial",
    label: "Industri i drift",
    hints: [
      "Samordningsansvar mot driftorganisationen",
      "Heta arbeten, processmedia och frånkoppling",
      "Interna trafikvägar och truckar",
      "Behörighet, inpassering och driftstopp",
    ],
  },
];
import { Users2, Plus, Trash2, AlertTriangle } from "lucide-react";
import { thirdPartyNeedsReview, formatDate } from "@/lib/basProcess/reminders";

interface Props {
  projectId: string;
}

interface MeasureRow {
  id: string;
  area: string;
  affected: string;
  risk: string;
  measure: string;
  responsible: string;
  done: boolean;
}

interface Content {
  situation?: string;
  occupied?: boolean;
  operationsRunning?: boolean;
  nightWork?: boolean;
  evacuation?: string;
  tempInstallations?: string;
  information?: string;
  contact?: string;
  measures?: MeasureRow[];
}

interface Plan {
  id: string;
  status: string;
  plan_kind: string;
  content_json: Content;
  last_stage_review_at: string | null;
  last_stage_review_by_name: string | null;
}

const newRow = (): MeasureRow => ({
  id: crypto.randomUUID(),
  area: "",
  affected: "",
  risk: "",
  measure: "",
  responsible: "",
  done: false,
});

const Toggle = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
    {label}
  </label>
);

const ThirdPartyPlan: React.FC<Props> = ({ projectId }) => {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [latestStage, setLatestStage] = useState<{ name: string; at: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("third_party_plans")
      .select("id, status, plan_kind, content_json, last_stage_review_at, last_stage_review_by_name")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(1);
    if (error) toast.error("Kunde inte läsa planen");
    const row = (data || [])[0] as unknown as Plan | undefined;
    setPlan(row ? { ...row, content_json: row.content_json || {} } : null);

    // Etappändringar ska lyfta planen som åtgärd hos Bas-P.
    const { data: stages } = await supabase
      .from("project_stages")
      .select("name, updated_at")
      .eq("project_id", projectId)
      .order("updated_at", { ascending: false })
      .limit(1);
    const latest = (stages || [])[0] as { name: string; updated_at: string } | undefined;
    setLatestStage(latest ? { name: latest.name, at: latest.updated_at } : null);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("third_party_plans").insert({
      project_id: projectId,
      created_by: auth.user?.id ?? null,
      plan_kind: "building",
      content_json: { measures: [newRow()] } as never,
    });
    if (error) return toast.error("Kunde inte skapa planen");
    load();
  };

  const setContent = async (content: Content) => {
    if (!plan) return;
    setPlan({ ...plan, content_json: content });
    const { error } = await supabase
      .from("third_party_plans")
      .update({ content_json: content } as never)
      .eq("id", plan.id);
    if (error) toast.error("Kunde inte spara");
  };

  const setKind = async (kind: string) => {
    if (!plan) return;
    setPlan({ ...plan, plan_kind: kind });
    const { error } = await supabase
      .from("third_party_plans")
      .update({ plan_kind: kind })
      .eq("id", plan.id);
    if (error) toast.error("Kunde inte spara typen");
  };

  const markStageReviewed = async () => {
    if (!plan) return;
    const { data: auth } = await supabase.auth.getUser();
    const name =
      (auth?.user?.user_metadata?.full_name as string | undefined) ||
      auth?.user?.email ||
      "okänd";
    const at = new Date().toISOString();
    const { error } = await supabase
      .from("third_party_plans")
      .update({ last_stage_review_at: at, last_stage_review_by_name: name })
      .eq("id", plan.id);
    if (error) return toast.error("Kunde inte spara avstämningen");
    setPlan({ ...plan, last_stage_review_at: at, last_stage_review_by_name: name });
    toast.success("Planen är avstämd mot etapperna");
  };

  if (loading) return <p className="py-10 text-center text-muted-foreground">Laddar…</p>;

  const c = plan?.content_json || {};
  const measures = c.measures || [];
  const openCount = measures.filter((m) => !m.done).length;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="07"
        icon={Users2}
        title="Tredje man och pågående verksamhet"
        description="Boende, personal, elever, kunder och förbipasserande som påverkas av arbetet. Här hanteras evakuering, tillfälliga installationer, arbetstider och information."
        legalRef="AFS 2023:3, AFS 2023:11"
        meta={
          plan ? (
            <Badge variant={openCount ? "destructive" : "secondary"}>
              {measures.length} åtgärder · {openCount} kvar
            </Badge>
          ) : undefined
        }
        actions={
          plan ? undefined : (
            <Button onClick={create}>
              <Plus className="mr-1 h-4 w-4" /> Skapa plan
            </Button>
          )
        }
      />

      {plan && thirdPartyNeedsReview(plan.last_stage_review_at, latestStage?.at ?? null) && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="space-y-1">
              <p className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                Etapperna har ändrats – stäm av planen för tredje man
              </p>
              <p className="text-xs text-muted-foreground">
                Senaste etappändring: {latestStage?.name} den {formatDate(latestStage?.at ?? null)}.{" "}
                {plan.last_stage_review_at
                  ? `Planen stämdes senast av ${formatDate(plan.last_stage_review_at)}${plan.last_stage_review_by_name ? ` av ${plan.last_stage_review_by_name}` : ""}.`
                  : "Planen har aldrig stämts av mot etapperna."}
              </p>
            </div>
            <Button size="sm" onClick={markStageReviewed}>
              Markera avstämd
            </Button>
          </CardContent>
        </Card>
      )}

      {!plan && (
        <Card>
          <CardContent className="space-y-3 py-12 text-center">
            <Users2 className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Ingen plan för tredje man ännu. Behövs så snart någon annan än byggarbetarna finns i
              eller intill arbetsområdet.
            </p>
            <Button onClick={create}>Skapa plan</Button>
          </CardContent>
        </Card>
      )}

      {plan && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Förutsättningar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Typ av omgivning</Label>
                  <Select value={plan.plan_kind || "building"} onValueChange={setKind}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLAN_KINDS.map((k) => (
                        <SelectItem key={k.value} value={k.value}>
                          {k.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-md border bg-muted/40 p-3">
                  <p className="text-xs font-medium">Tänk särskilt på</p>
                  <ul className="mt-1 list-disc pl-4 text-xs text-muted-foreground">
                    {(
                      PLAN_KINDS.find((k) => k.value === (plan.plan_kind || "building")) ??
                      PLAN_KINDS[0]
                    ).hints.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Toggle
                  label="Huset är bebott under arbetet"
                  checked={!!c.occupied}
                  onChange={(v) => setContent({ ...c, occupied: v })}
                />
                <Toggle
                  label="Verksamheten pågår under arbetet"
                  checked={!!c.operationsRunning}
                  onChange={(v) => setContent({ ...c, operationsRunning: v })}
                />
                <Toggle
                  label="Arbete på kvällar eller nätter"
                  checked={!!c.nightWork}
                  onChange={(v) => setContent({ ...c, nightWork: v })}
                />
              </div>
              <div>
                <Label>Vilka påverkas och hur?</Label>
                <Textarea
                  rows={3}
                  value={c.situation || ""}
                  onChange={(e) => setContent({ ...c, situation: e.target.value })}
                  placeholder="T.ex. 42 lägenheter bebodda under stambytet, barn och äldre i trapphuset dagtid."
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Evakuering och ersättningsutrymmen</Label>
                  <Textarea
                    rows={3}
                    value={c.evacuation || ""}
                    onChange={(e) => setContent({ ...c, evacuation: e.target.value })}
                    placeholder="T.ex. evakueringslägenhet under vecka 8–10, utrymningsväg via gården."
                  />
                </div>
                <div>
                  <Label>Tillfälliga installationer</Label>
                  <Textarea
                    rows={3}
                    value={c.tempInstallations || ""}
                    onChange={(e) => setContent({ ...c, tempInstallations: e.target.value })}
                    placeholder="T.ex. bodar med dusch och WC, provisorisk el och vatten, tillfällig belysning."
                  />
                </div>
                <div>
                  <Label>Information till berörda</Label>
                  <Textarea
                    rows={3}
                    value={c.information || ""}
                    onChange={(e) => setContent({ ...c, information: e.target.value })}
                    placeholder="T.ex. veckobrev i trapphus, anslag om bullrande arbeten 48 h i förväg."
                  />
                </div>
                <div>
                  <Label>Kontaktperson mot boende/verksamhet</Label>
                  <Input
                    value={c.contact || ""}
                    onChange={(e) => setContent({ ...c, contact: e.target.value })}
                    placeholder="Namn och telefon"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Åtgärder per område</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setContent({ ...c, measures: [...measures, newRow()] })}
              >
                <Plus className="mr-1 h-4 w-4" /> Lägg till åtgärd
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {measures.length === 0 && (
                <p className="text-sm text-muted-foreground">Inga åtgärder registrerade ännu.</p>
              )}
              {measures.map((m) => {
                const patch = (p: Partial<MeasureRow>) =>
                  setContent({
                    ...c,
                    measures: measures.map((x) => (x.id === m.id ? { ...x, ...p } : x)),
                  });
                return (
                  <div key={m.id} className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
                    <div>
                      <Label>Område</Label>
                      <Input
                        value={m.area}
                        onChange={(e) => patch({ area: e.target.value })}
                        placeholder="T.ex. trapphus B"
                      />
                    </div>
                    <div>
                      <Label>Vilka berörs</Label>
                      <Input
                        value={m.affected}
                        onChange={(e) => patch({ affected: e.target.value })}
                        placeholder="T.ex. boende plan 1–3"
                      />
                    </div>
                    <div>
                      <Label>Risk</Label>
                      <Textarea
                        rows={2}
                        value={m.risk}
                        onChange={(e) => patch({ risk: e.target.value })}
                        placeholder="T.ex. damm och blockerad utrymningsväg"
                      />
                    </div>
                    <div>
                      <Label>Åtgärd</Label>
                      <Textarea
                        rows={2}
                        value={m.measure}
                        onChange={(e) => patch({ measure: e.target.value })}
                        placeholder="T.ex. dammvägg med sluss, alternativ utrymning skyltas"
                      />
                    </div>
                    <div>
                      <Label>Ansvarig</Label>
                      <Input
                        value={m.responsible}
                        onChange={(e) => patch({ responsible: e.target.value })}
                        placeholder="Namn/roll"
                      />
                    </div>
                    <div className="flex items-end justify-between gap-2">
                      <Button
                        size="sm"
                        variant={m.done ? "secondary" : "default"}
                        onClick={() => patch({ done: !m.done })}
                      >
                        {m.done ? "Klar" : "Markera som klar"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setContent({ ...c, measures: measures.filter((x) => x.id !== m.id) })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ThirdPartyPlan;
